import { create } from "zustand";
import type { UploadQueueItem } from "@titancloud/shared";

interface UploadState {
  queue: Map<string, UploadQueueItem>;
  isVisible: boolean;
  completedCount: number;
  failedCount: number;
  totalCount: number;

  addItems: (items: UploadQueueItem[]) => void;
  updateItem: (id: string, updates: Partial<UploadQueueItem>) => void;
  removeItem: (id: string) => void;
  reset: () => void;
  setVisible: (visible: boolean) => void;
}

export const useUploadStore = create<UploadState>((set, get) => ({
  queue: new Map(),
  isVisible: false,
  completedCount: 0,
  failedCount: 0,
  totalCount: 0,

  addItems: (items) =>
    set((state) => {
      const next = new Map(state.queue);
      for (const item of items) next.set(item.id, item);
      // Fresh session when queue was empty — reset counters
      const isNewSession = state.queue.size === 0;
      return {
        queue: next,
        isVisible: true,
        totalCount: isNewSession ? items.length : state.totalCount + items.length,
        completedCount: isNewSession ? 0 : state.completedCount,
        failedCount: isNewSession ? 0 : state.failedCount,
      };
    }),

  updateItem: (id, updates) => {
    const state = get();
    const item = state.queue.get(id);
    if (!item) return;

    const next = new Map(state.queue);
    next.set(id, { ...item, ...updates });

    let { completedCount, failedCount } = state;

    if (updates.status === "done" && item.status !== "done") {
      completedCount++;
      setTimeout(() => get().removeItem(id), 1000);
    } else if (updates.status === "error" && item.status !== "error") {
      failedCount++;
      setTimeout(() => get().removeItem(id), 1000);
    }

    set({ queue: next, completedCount, failedCount });
  },

  removeItem: (id) =>
    set((state) => {
      const next = new Map(state.queue);
      next.delete(id);
      return { queue: next };
    }),

  reset: () =>
    set({ queue: new Map(), completedCount: 0, failedCount: 0, totalCount: 0, isVisible: false }),

  setVisible: (isVisible) => set({ isVisible }),
}));
